import { useAuth } from "@clerk/clerk-react";
import {
  AlertColor,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  MenuItem,
  Pagination,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AdminBentoCategory } from "schemas/admin";
import { COMPANY_TYPES, LOCATIONS } from "schemas/dashboard";

import Alert from "components/Alert";
import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import StyledPage from "components/Styled/Page";
import { fetcherAuth } from "utils/api";
import { useAlert } from "utils/useAlert";

type Mode = "brand_search" | "brand_notifications" | "user_requests";

type SetAlertFn = (message: string | JSX.Element, severity: AlertColor) => void;

type TriBool = true | false | null;

interface Geopoint {
  country_code: string;
  latitude?: number;
  longitude?: number;
  label?: string;
  is_country?: boolean;
}

interface TopLevelCategory {
  id: number;
  name: string;
  subcategories?: AdminBentoCategory[];
}

interface ClayBrand {
  id: number;
  brandName?: string;
  website?: string;
  instagramUsername?: string;
  tiktokUsername?: string;
  instagramFollowerCount?: number;
  mainCategory?: string;
  tier?: number;
  hasContacts?: boolean;
  hasNonGenericContacts?: boolean;
  worksWithUgc?: boolean;
  worksWithInfluencer?: boolean;
  hasAds?: boolean;
  isInternational?: boolean;
  updatedFromClayAt?: string | null;
}

interface PreviewResult {
  total: number;
  brands: ClayBrand[];
}

const POSITIVE_COMPANY_TYPES = COMPANY_TYPES.filter((ct) => ct.key !== "all");

const fmt = (n?: number) => {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

const BrandRow = ({ b }: { b: ClayBrand }) => {
  const flags = [
    b.worksWithUgc && "UGC",
    b.worksWithInfluencer && "Influencer",
    b.hasAds && "Ads",
    b.isInternational && "International",
    b.hasNonGenericContacts && "Has Direct Contact",
    b.hasContacts && !b.hasNonGenericContacts && "Has Contact",
  ].filter(Boolean) as string[];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 0.75,
        px: 1,
        borderBottom: "1px solid",
        borderColor: "divider",
        flexWrap: "wrap",
      }}
    >
      <Box sx={{ minWidth: 180, flex: "0 0 auto" }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {b.brandName || `#${b.id}`}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {b.instagramUsername && (
            <Box
              component="a"
              href={`https://instagram.com/${b.instagramUsername}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "inherit",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              @{b.instagramUsername}
            </Box>
          )}
          {b.instagramUsername && b.tiktokUsername && " · "}
          {b.tiktokUsername && `tt:${b.tiktokUsername}`}
        </Typography>
      </Box>

      {b.website && (
        <Box
          component="a"
          href={
            b.website.startsWith("http") ? b.website : `https://${b.website}`
          }
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            flex: "0 0 auto",
            maxWidth: 160,
            fontSize: 12,
            color: "text.secondary",
            textDecoration: "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {b.website.replace(/^https?:\/\//, "")}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, flex: "0 0 auto" }}>
        {fmt(b.instagramFollowerCount) && (
          <Typography variant="caption" color="text.secondary">
            {fmt(b.instagramFollowerCount)} followers
          </Typography>
        )}
        {b.tier != null && (
          <Typography variant="caption" color="text.secondary">
            · Tier {b.tier}
          </Typography>
        )}
        {b.mainCategory && (
          <Typography variant="caption" color="text.secondary">
            · {b.mainCategory}
          </Typography>
        )}
      </Box>

      {flags.length > 0 && (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {flags.map((f) => (
            <Chip
              key={f}
              label={f}
              size="small"
              sx={{ fontSize: 10, height: 18 }}
            />
          ))}
        </Box>
      )}

      {b.updatedFromClayAt && (
        <Chip
          label="Previously sent"
          size="small"
          color="warning"
          sx={{ fontSize: 10, height: 18, ml: "auto" }}
        />
      )}
    </Box>
  );
};

const AdminClay = () => {
  const [message, severity, setAlert, closeAlert] = useAlert();
  const [mode, setMode] = useState<Mode>("brand_search");

  return (
    <StyledPage
      content={
        <Box sx={{ mt: 2 }}>
          <Typography variant="h3" gutterBottom>
            <strong>Clay CSV Creation</strong>
          </Typography>
          <Tabs
            value={mode}
            onChange={(_, v) => setMode(v as Mode)}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab label="Brand Search" value="brand_search" />
            <Tab
              label="Pending Brand Notifications"
              value="brand_notifications"
            />
            <Tab label="Pending User Requests" value="user_requests" />
          </Tabs>

          {mode === "brand_search" && <BrandSearchExport setAlert={setAlert} />}
          {mode === "brand_notifications" && (
            <PendingExport
              previewUrl="/api/admin/clay/pending-brand-notifications/preview"
              createCsvUrl="/api/admin/clay/pending-brand-notifications/create-csv"
              entityLabel="brand notifications"
              renderRow={(r) => (
                <PendingBrandNotificationRow
                  key={r.bentoBrandId}
                  bentoBrandId={r.bentoBrandId as number}
                  brandName={r.brandName as string}
                  website={r.website as string}
                />
              )}
              setAlert={setAlert}
            />
          )}
          {mode === "user_requests" && (
            <PendingExport
              previewUrl="/api/admin/clay/pending-user-requests/preview"
              createCsvUrl="/api/admin/clay/pending-user-requests/create-csv"
              entityLabel="user requests"
              renderRow={(r) => (
                <PendingUserRequestRow
                  key={r.userRequestId}
                  userRequestId={r.userRequestId as number}
                  description={r.description as string}
                />
              )}
              setAlert={setAlert}
            />
          )}

          <Alert
            message={message}
            severity={severity}
            closeAlert={closeAlert}
          />
        </Box>
      }
    />
  );
};

const BrandSearchExport = ({ setAlert }: { setAlert: SetAlertFn }) => {
  const { getToken } = useAuth();

  // Filter state
  const [totalCount, setTotalCount] = useState<number>(100);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [locations, setLocations] = useState<string[]>(["all"]);
  const [companyTypes, setCompanyTypes] = useState<string[]>(["all"]);
  const [hasContact, setHasContact] = useState<TriBool>(false);
  const [hasDirectContact, setHasDirectContact] = useState<TriBool>(false);
  const [excludeAlreadySearched, setExcludeAlreadySearched] =
    useState<boolean>(true);
  const [excludeAlreadyExported, setExcludeAlreadyExported] =
    useState<boolean>(true);
  const [notifySlack, setNotifySlack] = useState<boolean>(true);
  const [includePlaceholderColumns, setIncludePlaceholderColumns] =
    useState<boolean>(false);
  const [geopoints, setGeopoints] = useState<string[]>([]);

  // Data
  const [topLevelCategories, setTopLevelCategories] = useState<
    TopLevelCategory[]
  >([]);
  const [expandedCats, setExpandedCats] = useState<Set<number>>(new Set());
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const previewPageSize = 50;

  // Loading
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);

  const selectedCatIds = useMemo(() => new Set(categoryIds), [categoryIds]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        "/api/admin/bento-categories",
        "GET",
      );
      setTopLevelCategories(res.topLevelCategories || []);
    } catch {
      // non-critical
    }
  }, [getToken]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const toggleCategory = (id: number) => {
    setCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleExpandCat = (id: number) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const buildFilters = () => ({
    search_query: searchQuery || null,
    category_ids: categoryIds.length ? categoryIds : null,
    locations,
    company_types: companyTypes,
    has_contact: hasContact,
    has_direct_contact: hasDirectContact,
    exclude_already_searched: excludeAlreadySearched,
    exclude_already_exported: excludeAlreadyExported,
    geopoints: geopoints.length ? geopoints : null,
  });

  const buildQueryParams = (page = 1) => {
    const params = new URLSearchParams();
    if (searchQuery) params.set("search_query", searchQuery);
    categoryIds.forEach((id) => params.append("category_ids", String(id)));
    locations.forEach((loc) => params.append("locations", loc));
    companyTypes.forEach((ct) => params.append("company_types", ct));
    if (hasContact !== null) params.set("has_contact", String(hasContact));
    if (hasDirectContact !== null)
      params.set("has_direct_contact", String(hasDirectContact));
    params.set("exclude_already_searched", String(excludeAlreadySearched));
    params.set("exclude_already_exported", String(excludeAlreadyExported));
    geopoints.forEach((gp) => params.append("geopoints", gp));
    params.set("page", String(page));
    params.set("page_size", String(previewPageSize));
    return params;
  };

  const fetchPreviewPage = async (page: number) => {
    setLoadingPreview(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/clay/preview?${buildQueryParams(page).toString()}`,
        "GET",
      );
      setPreview({ total: res.total, brands: res.brands });
      setPreviewPage(page);
    } catch {
      setAlert("Failed to load preview", "error");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePreview = () => {
    setPreview(null);
    fetchPreviewPage(1);
  };

  const handleCreateCsv = async () => {
    if (!preview) return;
    setLoadingCsv(true);
    try {
      await fetcherAuth(
        getToken,
        "/api/admin/clay/create-csv",
        "POST",
        {},
        {
          total_count: totalCount,
          filters: buildFilters(),
          notify_slack: notifySlack,
          include_placeholder_columns: includePlaceholderColumns,
        },
      );
      setAlert(
        notifySlack
          ? "Export started - a notification will be sent to #crm-uploads when finished."
          : "Export started.",
        "success",
      );
    } catch {
      setAlert("Failed to start CSV export", "error");
    } finally {
      setLoadingCsv(false);
    }
  };

  const triBoolValue = (v: TriBool) => (v === null ? "" : String(v));
  const parseTriBool = (s: string): TriBool => (s === "" ? null : s === "true");

  const handleMultiChange = (vals: string[], setter: (v: string[]) => void) => {
    if (vals[vals.length - 1] === "all" || vals.length === 0) {
      setter(["all"]);
    } else {
      setter(vals.filter((x) => x !== "all"));
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <TextField
          label="Total Count"
          type="number"
          size="small"
          value={totalCount}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v)) setTotalCount(v);
          }}
          sx={{ width: 130 }}
        />
        <TextField
          label="Search"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1 }}
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
          gap: 1,
          mb: 1,
        }}
      >
        <CompactSelect
          label="Company"
          multiple
          value={companyTypes}
          onChange={(v) => handleMultiChange(v as string[], setCompanyTypes)}
          options={[
            { value: "all", label: "All" },
            ...POSITIVE_COMPANY_TYPES.map((ct) => ({
              value: ct.key,
              label: ct.name,
            })),
          ]}
          renderValue={(sel) =>
            (sel as string[]).includes("all")
              ? "All"
              : `${(sel as string[]).length} sel.`
          }
        />
        <CompactSelect
          label="Location"
          multiple
          value={locations}
          onChange={(v) => handleMultiChange(v as string[], setLocations)}
          options={LOCATIONS.map((l) => ({ value: l.key, label: l.name }))}
          renderValue={(sel) =>
            (sel as string[]).includes("all")
              ? "All"
              : `${(sel as string[]).length} sel.`
          }
        />
        <CompactSelect
          label="Has Contact"
          value={triBoolValue(hasContact)}
          onChange={(v) => setHasContact(parseTriBool(v as string))}
          options={[
            { value: "", label: "Any" },
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]}
        />
        <CompactSelect
          label="Has Direct Contact"
          value={triBoolValue(hasDirectContact)}
          onChange={(v) => setHasDirectContact(parseTriBool(v as string))}
          options={[
            { value: "", label: "Any" },
            { value: "true", label: "Yes" },
            { value: "false", label: "No" },
          ]}
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <GeopointPicker geopoints={geopoints} onChange={setGeopoints} />
      </Box>

      {topLevelCategories.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: 10,
              display: "block",
              lineHeight: 1.2,
              mb: 0.5,
            }}
          >
            Categories ({categoryIds.length})
          </Typography>
          <Box
            sx={{
              maxHeight: 200,
              overflow: "auto",
              border: "1px solid",
              borderColor: grey[200],
              borderRadius: 1,
              p: 0.75,
            }}
          >
            {topLevelCategories.map((topCat) => (
              <Box key={topCat.id}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    "&:hover": { bgcolor: grey[50] },
                    borderRadius: 0.5,
                    px: 0.5,
                    py: 0.125,
                  }}
                  onClick={() => toggleExpandCat(topCat.id)}
                >
                  <Typography sx={{ fontWeight: 600, flex: 1, fontSize: 12 }}>
                    {topCat.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: 10 }}
                  >
                    {expandedCats.has(topCat.id) ? "−" : "+"}
                  </Typography>
                </Box>
                {expandedCats.has(topCat.id) &&
                  topCat.subcategories &&
                  topCat.subcategories.length > 0 && (
                    <Box sx={{ pl: 1.5, display: "flex", flexWrap: "wrap" }}>
                      {topCat.subcategories.map((sub) => (
                        <FormControlLabel
                          key={sub.id}
                          control={
                            <Checkbox
                              checked={selectedCatIds.has(sub.id)}
                              onChange={() => toggleCategory(sub.id)}
                              size="small"
                              sx={{ p: 0.25 }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: 11 }}>
                              {sub.displayName || sub.name}
                            </Typography>
                          }
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mr: 1.5,
                            my: 0,
                            minWidth: 0,
                          }}
                        />
                      ))}
                    </Box>
                  )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={excludeAlreadyExported}
              onChange={(e) => setExcludeAlreadyExported(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              Exclude brands already <strong>exported</strong> for Clay
            </Typography>
          }
        />
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={excludeAlreadySearched}
              onChange={(e) => setExcludeAlreadySearched(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              Exclude brands already <strong>updated</strong> from Clay
            </Typography>
          }
        />
      </Box>
      <Box>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={includePlaceholderColumns}
              onChange={(e) => setIncludePlaceholderColumns(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              Include placeholder contact columns (for testing only)
            </Typography>
          }
        />
      </Box>
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={notifySlack}
              onChange={(e) => setNotifySlack(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              Notify #crm-uploads in Slack when CSV is ready
            </Typography>
          }
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <Button
          size="small"
          variant="contained"
          onClick={handlePreview}
          disabled={loadingPreview}
          startIcon={loadingPreview ? <CircularProgress size={14} /> : null}
        >
          Preview Brands
        </Button>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={handleCreateCsv}
          disabled={!preview || loadingCsv}
          startIcon={loadingCsv ? <CircularProgress size={14} /> : null}
        >
          {preview
            ? `Create CSV (${Math.min(preview.total, totalCount).toLocaleString()} brands)`
            : "Create CSV"}
        </Button>
      </Box>

      {preview && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="h6">
              {(() => {
                const start = (previewPage - 1) * previewPageSize + 1;
                const end = Math.min(
                  previewPage * previewPageSize,
                  preview.total,
                );
                return (
                  <>
                    Showing {start.toLocaleString()}–{end.toLocaleString()} of{" "}
                    <strong>{preview.total.toLocaleString()}</strong> brands
                  </>
                );
              })()}
            </Typography>
            {loadingPreview && <CircularProgress size={16} />}
          </Box>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              mb: 1,
            }}
          >
            {preview.brands.map((b) => (
              <BrandRow key={b.id} b={b} />
            ))}
          </Box>
          {preview.total > previewPageSize && (
            <Pagination
              count={Math.ceil(preview.total / previewPageSize)}
              page={previewPage}
              onChange={(_, p) => fetchPreviewPage(p)}
              size="small"
              disabled={loadingPreview}
            />
          )}
        </>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// GeopointPicker
// ---------------------------------------------------------------------------

function GeopointPicker({
  geopoints,
  onChange,
}: {
  geopoints: string[];
  onChange: (geopoints: string[]) => void;
}) {
  const geopointsRef = useRef<string[]>(geopoints);
  useEffect(() => {
    geopointsRef.current = geopoints;
  }, [geopoints]);

  const parsed = useMemo(() => {
    return geopoints
      .map((g) => {
        try {
          return JSON.parse(g) as Geopoint;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as Geopoint[];
  }, [geopoints]);

  const handleLocationChange = (userLocation: UserLocation) => {
    const current = geopointsRef.current || [];
    const currentParsed: Geopoint[] = current
      .map((g) => {
        try {
          return JSON.parse(g) as Geopoint;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as Geopoint[];

    const addressComponents = userLocation?.address_components;

    let city = addressComponents?.find(
      (x) =>
        x?.types?.includes("administrative_area_level_3") ||
        x?.types?.includes("locality"),
    )?.longText;

    const region = addressComponents?.find((x) =>
      x?.types?.includes("administrative_area_level_1"),
    )?.longText;

    let isCountry = false;
    if (!city && !region) {
      city = addressComponents?.[0]?.longText;
      isCountry = true;
    }

    if (
      userLocation?.country_code &&
      userLocation.latitude &&
      userLocation.longitude
    ) {
      const geopoint: Geopoint = {
        country_code: userLocation.country_code,
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        is_country: isCountry,
        label: city || region,
      };

      const exists = currentParsed.some(
        (g) =>
          g.latitude === geopoint.latitude &&
          g.longitude === geopoint.longitude,
      );
      if (!exists) {
        onChange([...current, JSON.stringify(geopoint)]);
      }
    }
  };

  const removeGeopoint = (index: number) => {
    const current = geopointsRef.current || [];
    onChange(current.filter((_, i) => i !== index));
  };

  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, display: "block", lineHeight: 1.2 }}
      >
        Cities / Countries
      </Typography>
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          border: "1px solid",
          borderColor: grey[300],
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <LocationAutocomplete
          handleLocationChange={handleLocationChange}
          customStyling={{
            border: "none",
            borderRadius: 1,
            height: 28,
            py: 0.25,
            px: 1,
            width: "100%",
            fontSize: 12,
            "&:hover, &:focus": { outline: "none" },
          }}
          placeholderText="Add city or country..."
        />
      </Box>

      {parsed.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
          {parsed.map((gp, i) => (
            <Chip
              key={i}
              label={
                gp.label ? `${gp.label} (${gp.country_code})` : gp.country_code
              }
              size="small"
              onDelete={() => removeGeopoint(i)}
              sx={{ fontSize: 11, height: 22 }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CompactSelect
// ---------------------------------------------------------------------------

function CompactSelect({
  label,
  multiple,
  value,
  onChange,
  options,
  renderValue,
}: {
  label: string;
  multiple?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  options: { value: string; label: string }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderValue?: (value: any) => React.ReactNode;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, display: "block", lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Select
        multiple={multiple}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        displayEmpty
        renderValue={renderValue}
        sx={{ "& .MuiSelect-select": { py: 0.5, fontSize: 12 } }}
      >
        {options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}

interface PendingRow {
  [key: string]: string | number;
}

interface PendingPreviewResult {
  total: number;
  rows: PendingRow[];
}

const PendingExport = ({
  previewUrl,
  createCsvUrl,
  entityLabel,
  renderRow,
  setAlert,
}: {
  previewUrl: string;
  createCsvUrl: string;
  entityLabel: string;
  renderRow: (row: PendingRow) => JSX.Element;
  setAlert: SetAlertFn;
}) => {
  const { getToken } = useAuth();
  const [totalCount, setTotalCount] = useState<number>(100);
  const [notifySlack, setNotifySlack] = useState<boolean>(true);
  const [preview, setPreview] = useState<PendingPreviewResult | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const previewPageSize = 50;
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);

  const fetchPreviewPage = async (page: number) => {
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("page_size", String(previewPageSize));
      const res = await fetcherAuth(
        getToken,
        `${previewUrl}?${params.toString()}`,
        "GET",
      );
      setPreview({ total: res.total, rows: res.rows });
      setPreviewPage(page);
    } catch {
      setAlert("Failed to load preview", "error");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handlePreview = () => {
    setPreview(null);
    fetchPreviewPage(1);
  };

  const handleCreateCsv = async () => {
    if (!preview) return;
    setLoadingCsv(true);
    try {
      await fetcherAuth(
        getToken,
        createCsvUrl,
        "POST",
        {},
        {
          total_count: totalCount,
          notify_slack: notifySlack,
        },
      );
      setAlert(
        notifySlack
          ? "Export started - a notification will be sent to #crm-uploads when finished."
          : "Export started.",
        "success",
      );
    } catch {
      setAlert("Failed to start CSV export", "error");
    } finally {
      setLoadingCsv(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <TextField
          label="Total Count"
          type="number"
          size="small"
          value={totalCount}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v)) setTotalCount(v);
          }}
          sx={{ width: 130 }}
        />
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={notifySlack}
              onChange={(e) => setNotifySlack(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">
              Notify #crm-uploads in Slack when CSV is ready
            </Typography>
          }
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <Button
          size="small"
          variant="contained"
          onClick={handlePreview}
          disabled={loadingPreview}
          startIcon={loadingPreview ? <CircularProgress size={14} /> : null}
        >
          Preview {entityLabel}
        </Button>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={handleCreateCsv}
          disabled={!preview || loadingCsv}
          startIcon={loadingCsv ? <CircularProgress size={14} /> : null}
        >
          {preview
            ? `Create CSV (${Math.min(preview.total, totalCount).toLocaleString()})`
            : "Create CSV"}
        </Button>
      </Box>

      {preview && (
        <>
          <Divider sx={{ mb: 1 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="h6">
              {(() => {
                const start = (previewPage - 1) * previewPageSize + 1;
                const end = Math.min(
                  previewPage * previewPageSize,
                  preview.total,
                );
                return (
                  <>
                    Showing {start.toLocaleString()}–{end.toLocaleString()} of{" "}
                    <strong>{preview.total.toLocaleString()}</strong>{" "}
                    {entityLabel}
                  </>
                );
              })()}
            </Typography>
            {loadingPreview && <CircularProgress size={16} />}
          </Box>
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              mb: 1,
            }}
          >
            {preview.rows.map((r) => renderRow(r))}
          </Box>
          {preview.total > previewPageSize && (
            <Pagination
              count={Math.ceil(preview.total / previewPageSize)}
              page={previewPage}
              onChange={(_, p) => fetchPreviewPage(p)}
              size="small"
              disabled={loadingPreview}
            />
          )}
        </>
      )}
    </Box>
  );
};

const PendingBrandNotificationRow = ({
  bentoBrandId,
  brandName,
  website,
}: {
  bentoBrandId: number;
  brandName: string;
  website: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1.5,
      py: 0.75,
      px: 1,
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60 }}>
      #{bentoBrandId}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 200 }} noWrap>
      {brandName || `Brand ${bentoBrandId}`}
    </Typography>
    {website && (
      <Box
        component="a"
        href={website.startsWith("http") ? website : `https://${website}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          fontSize: 12,
          color: "text.secondary",
          textDecoration: "none",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {website.replace(/^https?:\/\//, "")}
      </Box>
    )}
  </Box>
);

const PendingUserRequestRow = ({
  userRequestId,
  description,
}: {
  userRequestId: number;
  description: string;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      gap: 1.5,
      py: 0.75,
      px: 1,
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ minWidth: 60, mt: 0.25 }}
    >
      #{userRequestId}
    </Typography>
    <Typography variant="body2" sx={{ flex: 1, whiteSpace: "pre-wrap" }}>
      {description}
    </Typography>
  </Box>
);

export default AdminClay;
